<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import {
  arrowsAiChannelKey,
  useArrowsAiAccount,
} from 'dashboard/composables/useArrowsAiAccount';
import PageLayout from 'dashboard/components-next/arrowsAi/PageLayout.vue';
import EmptyState from 'dashboard/components-next/arrowsAi/EmptyState.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';
import InboxCard from 'dashboard/components-next/arrowsAi/assistant/InboxCard.vue';
import ConnectInboxDialog from 'dashboard/components-next/arrowsAi/assistant/ConnectInboxDialog.vue';
import QuotaLockedDialog from 'dashboard/components-next/arrowsAi/assistant/QuotaLockedDialog.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { allowedChannelTypes, inboxesPerAssistant } = useArrowsAiAccount();
const inboxes = useMapGetter('inboxes/getInboxes');
const {
  assistants,
  getAssistant,
  rememberAssistant,
  refreshAssistants,
  connectInbox,
  disconnectInbox,
  canAssignInbox,
} = useArrowsAiAssistants();

onMounted(() => {
  store.dispatch('accounts/get', { silent: true });
  store.dispatch('inboxes/get');
  refreshAssistants();
});

const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() => {
  const current = getAssistant(assistantId.value);
  if (current) rememberAssistant(current.id);
  return current;
});

const connectedInboxes = computed(() => {
  const ids = assistant.value?.inboxIds || [];
  return inboxes.value.filter(inbox => ids.includes(inbox.id));
});

const takenInboxIds = computed(() => {
  const currentId = assistant.value?.id;
  return new Set(
    assistants.value
      .filter(item => item.id !== currentId)
      .flatMap(item => item.inboxIds || [])
  );
});

const availableInboxes = computed(() => {
  const ids = assistant.value?.inboxIds || [];
  return inboxes.value
    .filter(
      inbox =>
        !ids.includes(inbox.id) &&
        !takenInboxIds.value.has(inbox.id) &&
        allowedChannelTypes.value.includes(arrowsAiChannelKey(inbox))
    )
    .map(inbox => ({
      value: inbox.id,
      label: inbox.name,
      conflicts:
        inbox.connected_automation_kinds ||
        inbox.connectedAutomationKinds ||
        [],
    }));
});

const isEmpty = computed(() =>
  Boolean(assistant.value && !connectedInboxes.value.length)
);
const showConnect = computed(
  () =>
    Boolean(assistant.value) &&
    connectedInboxes.value.length < inboxesPerAssistant.value
);
const emptyTitle = computed(() =>
  canAssignInbox.value
    ? t('ARROWS_AI.INBOXES.EMPTY_TITLE')
    : t('ARROWS_AI.INBOXES.QUOTA_TITLE')
);
const supportLabel = computed(() => t('ARROWS_AI.INBOXES.CONTACT_SUPPORT'));
const emptySubtitle = computed(() =>
  canAssignInbox.value
    ? t('ARROWS_AI.INBOXES.EMPTY_SUBTITLE')
    : t('ARROWS_AI.INBOXES.QUOTA_SUBTITLE', { support: supportLabel.value })
);
const connectBlockedMessage = computed(() =>
  canAssignInbox.value
    ? t('ARROWS_AI.INBOXES.LIMIT_REACHED', {
        count: inboxesPerAssistant.value,
      })
    : t('ARROWS_AI.INBOXES.QUOTA_LOCKED', { support: supportLabel.value })
);
const dialogRef = ref(null);
const quotaDialogRef = ref(null);

const openConnect = () => {
  if (!showConnect.value) {
    useAlert(
      t('ARROWS_AI.INBOXES.LIMIT_REACHED', {
        count: inboxesPerAssistant.value,
      })
    );
    return;
  }
  if (!canAssignInbox.value) {
    nextTick(() => quotaDialogRef.value?.dialogRef?.open());
    return;
  }
  nextTick(() => dialogRef.value.dialogRef.open());
};

const inboxName = inboxId => {
  const connected = connectedInboxes.value.find(
    inbox => Number(inbox.id) === Number(inboxId)
  );
  if (connected) return connected.name;

  const option = availableInboxes.value.find(
    inbox => Number(inbox.value) === Number(inboxId)
  );
  return option?.label || String(inboxId);
};

const handleConnect = async inboxId => {
  const name = inboxName(inboxId);
  try {
    const updated = await connectInbox(assistant.value.id, inboxId);
    if (!updated) {
      useAlert(connectBlockedMessage.value);
      return;
    }
    useAlert(t('ARROWS_AI.INBOXES.SUCCESS', { name }));
    dialogRef.value.dialogRef.close();
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.SETTINGS.ERROR'));
  }
};

const handleDisconnect = async inboxId => {
  const name = inboxName(inboxId);
  try {
    await disconnectInbox(assistant.value.id, inboxId);
    useAlert(t('ARROWS_AI.INBOXES.DISCONNECTED', { name }));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.SETTINGS.ERROR'));
  }
};
</script>

<template>
  <PageLayout
    :header-title="t('ARROWS_AI.INBOXES.TITLE')"
    :button-label="showConnect ? t('ARROWS_AI.INBOXES.ADD') : ''"
    :is-empty="isEmpty"
    @click="openConnect"
  >
    <template #emptyState>
      <EmptyState
        :title="emptyTitle"
        :subtitle="emptySubtitle"
        :action-label="showConnect ? t('ARROWS_AI.INBOXES.ADD') : ''"
        @click="openConnect"
      />
    </template>
    <MissingAssistant v-if="!assistant" />
    <div v-else class="flex flex-col gap-4">
      <InboxCard
        v-for="inbox in connectedInboxes"
        :key="inbox.id"
        :inbox="inbox"
        @disconnect="handleDisconnect"
      />
    </div>
  </PageLayout>
  <ConnectInboxDialog
    ref="dialogRef"
    :options="availableInboxes"
    @submit="handleConnect"
  />
  <QuotaLockedDialog ref="quotaDialogRef" />
</template>
